import React, { useEffect } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import '../scss/pension-page.scss';

// Import pension page images
import {
  pensionHeroImage,
  walletIcon,
  receiptIcon,
  startupIcon,
  globalIcon,
  filesIcon,
  arrowIcon,
  ellipseIcon,
  fallbackWalletIcon,
  fallbackReceiptIcon,
  fallbackStartupIcon,
  fallbackGlobalIcon,
  fallbackFilesIcon,
  fallbackStepBg,
  fallbackHeroImage,
  fallbackArrow
} from './images/jpg/pensionPageImages';

interface PensionPageProps {
  title?: string;
  subtitle?: string;
}

export type { PensionPageProps };

const PensionPage: React.FC<PensionPageProps> = ({
  title,
  subtitle
}) => {
  const { t } = useTranslation();

  // Test image URLs on component mount
  useEffect(() => {
    // Create a test image to check if it loads
    const testImg = new Image();
    testImg.onload = () => {
      console.log('✅ Hero image URL is accessible');
    };
    testImg.onerror = () => {
      console.warn('❌ Hero image URL failed to load, fallback will be used');
    };
    testImg.src = pensionHeroImage;
  }, []);

  const benefitsData = [
    {
      icon: walletIcon,
      title: t("PENSION_BENEFIT_1_TITLE"),
      description: t("PENSION_BENEFIT_1_DESCRIPTION"),
      fallback: fallbackWalletIcon
    },
    {
      icon: receiptIcon,
      title: t("PENSION_BENEFIT_2_TITLE"),
      description: t("PENSION_BENEFIT_2_DESCRIPTION"),
      fallback: fallbackReceiptIcon
    },
    {
      icon: startupIcon,
      title: t("PENSION_BENEFIT_3_TITLE"),
      description: t("PENSION_BENEFIT_3_DESCRIPTION"),
      fallback: fallbackStartupIcon
    },
    {
      icon: globalIcon,
      title: t("PENSION_BENEFIT_4_TITLE"),
      description: t("PENSION_BENEFIT_4_DESCRIPTION"),
      fallback: fallbackGlobalIcon
    },
    {
      icon: filesIcon,
      title: t("PENSION_BENEFIT_5_TITLE"),
      description: t("PENSION_BENEFIT_5_DESCRIPTION"),
      fallback: fallbackFilesIcon
    }
  ];

  const stepsData = [
    {
      number: "1",
      title: t("PENSION_STEP_1_TITLE"),
      description: t("PENSION_STEP_1_DESCRIPTION")
    },
    {
      number: "2", 
      title: t("PENSION_STEP_2_TITLE"),
      description: t("PENSION_STEP_2_DESCRIPTION")
    },
    {
      number: "3",
      title: t("PENSION_STEP_3_TITLE"), 
      description: t("PENSION_STEP_3_DESCRIPTION")
    }
  ];

  return (
    <div className="pension-page">
      {/* Main Content Section */}
      <section className="pension-main-content">
        <Container >
          <Row className='px-0'>
            <Col xs={12} lg={6} className="mb-5 mb-lg-0">
              <h2 className="pension-main-title">
                {t("PENSION_MAIN_TITLE")}
              </h2>
            </Col>
            <Col xs={12} lg={6}>
              <p className="h6-light text-gray-700 mb-0">
                {t("PENSION_MAIN_DESCRIPTION")}
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Hero Image Section */}
      <section className="pension-hero-image">
        <Container>
          <Row>
            <Col xs={12}>
              <div className="hero-image-container">
                <img 
                  src={pensionHeroImage} 
                  alt="Pension consultation" 
                  className="hero-image"
                  loading="lazy"
                  style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Benefits Section */}
      <section className="pension-benefits px-0">
        <Container className='px-0'>
          <Row className="justify-content-center mb-5">
            <Col xs={12} lg={8} className="text-center">
              <h2 className="h2-bold text-gray-900 mb-0">
                {t("PENSION_BENEFITS_TITLE")}
              </h2>
            </Col>
          </Row>
          <Row className="benefits-grid justify-content-center px-0">
            {benefitsData.map((benefit, index) => (
              <Col xs={12} sm={6} md={4} lg={4} xl={2} key={index} className="mb-5 benefit-col">
                <div className="benefit-item text-center">
                  <div className="benefit-icon mb-4">
                    <img 
                      src={benefit.icon} 
                      alt={benefit.title}
                      loading="eager"
                      style={{
                        display: 'block',
                        visibility: 'visible',
                        opacity: 1,
                        width: '52px',
                        height: '52px',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        console.log(`Figma icon failed for ${benefit.title}, using fallback`);
                        e.currentTarget.src = benefit.fallback;
                        e.currentTarget.style.display = 'block';
                      }}
                      onLoad={(e) => {
                        console.log(`Icon loaded successfully for: ${benefit.title}`);
                        e.currentTarget.style.display = 'block';
                      }}
                    />
                  </div>
                  <div className="benefit-content">
                    <h5 className="h5-bold text-gray-900 mb-3">{benefit.title}</h5>
                    <p className="body-text text-gray-700 mb-0">{benefit.description}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Why Important Section */}
      <section className="pension-why-important">
        <Container>
          <Row>
            <Col xs={12} lg={6} className="mb-5 mb-lg-0">
              <h2 className="h2-bold text-gray-900 mb-0">
                {t("PENSION_WHY_IMPORTANT_TITLE")}
              </h2>
            </Col>
            <Col xs={12} lg={6}>
              <div className="content-section">
                <p className="h6-light text-gray-700 mb-4">
                  {t("PENSION_WHY_IMPORTANT_DESCRIPTION")}
                </p>
                <Button 
                  variant="link" 
                  className="btn-link-brand p-0"
                  onClick={() => window.open('https://www.api.invozio.com/img/Tj%C3%A4nstepension.pdf?_gl=1*1s38s6w*_gcl_au*MTU4ODAwMTk2NS4xNzU0MDI2NDUy*_ga*MTQyNTE0OTA3Mi4xNzU0MDI2NDUy*_ga_DJKEC77RNR*czE3NTU1MzA3NTIkbzI3JGcxJHQxNzU1NTMwNzg4JGoyNCRsMCRoMA..', '_blank')}
                >
                  {t("PENSION_READ_MORE")}
                  <img 
                    src={arrowIcon} 
                    alt="Arrow" 
                    className="ms-2" 
                    width="20" 
                    height="20"
                    loading="lazy"
                    onError={(e) => {
                      console.error('Failed to load arrow icon:', arrowIcon);
                      e.currentTarget.src = fallbackArrow;
                      e.currentTarget.style.display = 'inline-block';
                    }}
                    onLoad={(e) => {
                      console.log('Arrow icon loaded:', arrowIcon);
                      e.currentTarget.style.display = 'inline-block';
                    }}
                  />
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works Section */}
      <section className="pension-how-it-works">
        <Container className="px-0">
          <Row>
            <Col xs={12}>
              <h2 className="how-it-works-title">{t("PENSION_HOW_IT_WORKS_TITLE")}</h2>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="steps-container">
                {stepsData.map((step, index) => (
                  <div key={index} className="step-item">
                    <div className="step-number">
                      <div className="step-bg"></div>
                      <span className="step-text">{step.number}</span>
                    </div>
                    <div className="step-content">
                      <h5 className="step-title">{step.title}</h5>
                      <p className="step-description">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
     
    </div>
  );
};

export default PensionPage;
export { PensionPage };
